import React from "react";
import { HighlightText } from "../../components/HighlightText";
import { interpolate, spring, useVideoConfig } from "remotion";
import { BulletItem } from "../../types";
import { CompanyBadge } from "../../components/CompanyBadge";
import { detectBrandInText } from "../../components/companyDetector";

interface SolarFlareInsightsProps {
  bullets: BulletItem[];
  globalFrame: number;
}

export const SolarFlareInsights: React.FC<SolarFlareInsightsProps> = ({
  bullets,
  globalFrame,
}) => {
  const { fps } = useVideoConfig();
  const currentMs = (globalFrame / fps) * 1000;

  let activeIndex = bullets.findIndex(
    (b) => currentMs >= b.startMs && currentMs <= b.endMs
  );

  if (activeIndex === -1) {
    const pastBullets = bullets.filter((b) => b.endMs <= currentMs);
    activeIndex = pastBullets.length > 0 ? pastBullets.length - 1 : 0;
  }

  const activeBullet = bullets[activeIndex];
  const bulletStartFrame = (activeBullet.startMs / 1000) * fps;
  const elapsedFrames = Math.max(0, globalFrame - bulletStartFrame);

  // Bullet entrance animation
  const opacity = interpolate(elapsedFrames, [0, 10], [0, 1]);
  const translateX = spring({
    frame: elapsedFrames,
    fps,
    from: 100,
    to: 0,
    config: { stiffness: 120, damping: 14, mass: 0.5 },
  });
  const rotation = interpolate(elapsedFrames, [0, 15], [-3, 0], {
    extrapolateRight: "clamp",
  });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        padding: "0 50px",
        zIndex: 10,
        margin: "auto 0",
      }}
    >
      <div
        style={{
          width: "100%",
          background: "rgba(5, 2, 0, 0.85)",
          border: "2px solid #FF4500",
          borderRadius: "8px",
          boxShadow: "0 0 30px rgba(255, 69, 0, 0.2)",
          padding: "40px",
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          opacity,
          transform: `translateX(${translateX}px) rotate(${rotation}deg)`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            {bullets.map((_, i) => (
              <div
                key={i}
                style={{
                  width: i === activeIndex ? "40px" : "12px",
                  height: "12px",
                  borderRadius: "6px",
                  background: i === activeIndex ? "#FF4500" : "rgba(255, 255, 255, 0.1)",
                  transition: "width 0.3s ease",
                }}
              />
            ))}
          </div>
          {(() => {
            const brand = detectBrandInText(activeBullet.text);
            return brand ? (
              <CompanyBadge
                name={brand}
                color="#FF4500"
                accentColor="#FF4500"
                size={72}
                style="glow"
                opacity={opacity}
              />
            ) : null;
          })()}
        </div>

        <div style={{ minHeight: "260px", display: "flex", alignItems: "center" }}>
          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "48px",
              fontWeight: 800,
              lineHeight: 1.2,
              color: "#FFFFFF",
              margin: 0,
              textAlign: "left",
            }}
          >
            <HighlightText
              highlightStyle={{
                color: "#FFD700",
                textShadow: "0 0 15px rgba(255, 215, 0, 0.5)",
              }}
              text={activeBullet.text}
              highlights={activeBullet.highlights}
            />
          </p>
        </div>
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
